import { Path } from 'paper';

import { type GenericFreedrawElement } from '..';
import { reversionElement } from '../../../lib/element';
import { getBoundsFromPoints } from '../../../utils/editor/bounds';
import { type Point, arePointsEqual } from '../../../utils/math';
import { type PointBezierHandle } from '../../poly';

export const simplifyPoints = (
  points: Point[],
): {
  points: Point[];
  bezierHandles: PointBezierHandle[];
} => {
  const handles = points.map((point) => ({
    before: point,
    after: point,
  }));

  const paperPath = new Path();

  paperPath.moveTo([points[0][0], points[0][1]]);
  points.forEach((_, pointIndex) => {
    if (pointIndex === points.length - 1) return;
    const point = points[pointIndex + 1];
    paperPath.cubicCurveTo(
      [handles[pointIndex].after[0], handles[pointIndex].after[1]],
      [handles[pointIndex + 1].before[0], handles[pointIndex + 1].before[1]],
      [point[0], point[1]],
    );
  });

  paperPath.simplify();

  const { newHandles, newPoints } = paperPath.segments.reduce<{
    newHandles: PointBezierHandle[];
    newPoints: Point[];
  }>(
    (acc, curr) => {
      acc.newPoints.push([curr.point.x, curr.point.y]);
      acc.newHandles.push({
        before: [
          curr.handleIn.x + curr.point.x,
          curr.handleIn.y + curr.point.y,
        ],
        after: [
          curr.handleOut.x + curr.point.x,
          curr.handleOut.y + curr.point.y,
        ],
      });
      return acc;
    },
    {
      newHandles: [],
      newPoints: [],
    },
  );

  return {
    bezierHandles: newHandles,
    points: newPoints,
  };
};

export const mergeStrokes = <T extends GenericFreedrawElement>(
  stroke1: T,
  stroke2: T,
): T => {
  const absPoints2: Point[] = stroke2.points.map((p) => [
    p[0] + stroke2.x,
    p[1] + stroke2.y,
  ]);

  let absPoints1: Point[] = stroke1.points.map((p) => [
    p[0] + stroke1.x,
    p[1] + stroke1.y,
  ]);

  const hasOverlap = arePointsEqual(absPoints1.at(0), absPoints2.at(-1));
  if (hasOverlap) absPoints1 = absPoints1.slice(1);

  const allAbsPoints = [...absPoints2, ...absPoints1];
  const [minX, minY, maxX, maxY] = getBoundsFromPoints(allAbsPoints);

  const mergedPoints: Point[] = allAbsPoints.map((p) => [
    p[0] - minX,
    p[1] - minY,
  ]);

  let mergedHandles: PointBezierHandle[] | undefined = undefined;

  if (stroke2.bezierHandles) {
    const dx2 = stroke2.x - minX;
    const dy2 = stroke2.y - minY;
    const handles2: PointBezierHandle[] = stroke2.bezierHandles.map((h) => ({
      before: [h.before[0] + dx2, h.before[1] + dy2] as Point,
      after: [h.after[0] + dx2, h.after[1] + dy2] as Point,
    }));

    const dx1 = stroke1.x - minX;
    const dy1 = stroke1.y - minY;
    const handles1: PointBezierHandle[] = stroke1.points.slice(1).map((p) => ({
      before: [p[0] + dx1, p[1] + dy1] as Point,
      after: [p[0] + dx1, p[1] + dy1] as Point,
    }));

    mergedHandles = [...handles2, ...handles1];
  }

  return {
    ...stroke2,
    x: minX,
    y: minY,
    originX: minX,
    originY: minY,
    width: maxX - minX,
    height: maxY - minY,
    points: mergedPoints,
    bezierHandles: mergedHandles,
    ...reversionElement(stroke2, ['bounds', 'canvas']),
  };
};

export const canMergeStrokes = <T extends GenericFreedrawElement>(
  stroke1: T,
  stroke2?: T | null,
): stroke2 is T => {
  return (
    !!stroke2 &&
    stroke1.strokeColor === stroke2.strokeColor &&
    stroke1.strokeWidth === stroke2.strokeWidth &&
    stroke1.strokeCap === stroke2.strokeCap
  );
};
